import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { GuidedAnswer, GuidedModeDefinition, GuidedModeStep } from '../types/guided-mode.types'
import { findNextUnansweredStepIndex, isStepAnswered, isStepVisible, stepKey } from '../utils/step-engine'
import type { GuidedModeAnswerAttributes } from '../utils/step-engine'

interface UseGuidedReviewFlowOptions {
  answers: Ref<Record<string, GuidedAnswer>>
  answerAttributes: ComputedRef<GuidedModeAnswerAttributes>
  currentIndex: Ref<number>
  definition: ComputedRef<GuidedModeDefinition | null>
  visibleSteps: ComputedRef<GuidedModeStep[]>
  replaceAnswerAttributes: (attributes: GuidedModeAnswerAttributes) => void
}

interface VisibleReviewStep {
  key: string
  step: GuidedModeStep
  topLevelIndex: number
}

export function useGuidedReviewFlow(options: UseGuidedReviewFlowOptions) {
  const {
    answers, answerAttributes, currentIndex, definition, visibleSteps, replaceAnswerAttributes,
  } = options
  const editStepKey = ref<string | null>(null)
  const showFollowUpWarning = ref(false)
  let answersSnapshot: Record<string, GuidedAnswer> | null = null
  let answerAttributesSnapshot: GuidedModeAnswerAttributes = {}
  let initialVisibleReviewStepKeys = new Set<string>()
  let editedStepId: string | undefined

  const editing = computed(() => editStepKey.value !== null)
  const currentStepIndex = computed(() => {
    if (!editStepKey.value) return currentIndex.value
    const index = visibleSteps.value.findIndex(step => stepKey(step) === editStepKey.value)
    return index >= 0 ? index : currentIndex.value
  })

  function begin(stepIndex: number): boolean {
    const step = visibleSteps.value[stepIndex]
    if (!step) return false
    currentIndex.value = stepIndex
    editStepKey.value = stepKey(step)
    editedStepId = step.id
    answersSnapshot = cloneAnswers(answers.value)
    answerAttributesSnapshot = { ...answerAttributes.value }
    initialVisibleReviewStepKeys = new Set(
      visibleReviewSteps(visibleSteps.value, answers.value).map(item => item.key),
    )
    showFollowUpWarning.value = false
    return true
  }

  function requiresFollowUpConfirmation(): boolean {
    if (!editStepKey.value || !answersSnapshot || showFollowUpWarning.value) return false
    const requiresFollowUp = visibleReviewSteps(visibleSteps.value, answers.value).some(item => (
      item.topLevelIndex > currentStepIndex.value
      && !initialVisibleReviewStepKeys.has(item.key)
      && !isStepAnswered(item.step, definition.value, answers.value, answerAttributes.value)
    ))
    if (requiresFollowUp) showFollowUpWarning.value = true
    return requiresFollowUp
  }

  function cancelChange(): boolean {
    if (answersSnapshot) {
      answers.value = cloneAnswers(answersSnapshot)
      replaceAnswerAttributes(answerAttributesSnapshot)
    }
    reset()
    return true
  }

  function continueChange() {
    showFollowUpWarning.value = false
    answersSnapshot = null
    answerAttributesSnapshot = {}
    initialVisibleReviewStepKeys = new Set()
  }

  function nextUnansweredIndex(): number {
    return findNextUnansweredStepIndex(
      visibleSteps.value,
      definition.value,
      answers.value,
      answerAttributes.value,
      currentStepIndex.value,
    )
  }

  // An eye list edited from the summary also opens its answered eye step, so the eye can change.
  function nextReviewIndex(): number {
    const unansweredIndex = nextUnansweredIndex()
    const attributeIndex = visibleSteps.value.findIndex((step, index) => (
      index > currentStepIndex.value && step.kind === 'attribute'
      && editedStepId !== undefined && step.sourceGroup === editedStepId
    ))
    if (attributeIndex < 0) return unansweredIndex
    return unansweredIndex < 0 ? attributeIndex : Math.min(attributeIndex, unansweredIndex)
  }

  function moveToStep(index: number) {
    currentIndex.value = index
    if (editStepKey.value && visibleSteps.value[index]) {
      editStepKey.value = stepKey(visibleSteps.value[index])
    }
  }

  function reset() {
    editStepKey.value = null
    editedStepId = undefined
    answersSnapshot = null
    answerAttributesSnapshot = {}
    initialVisibleReviewStepKeys = new Set()
    showFollowUpWarning.value = false
  }

  return {
    editing,
    editStepKey,
    showFollowUpWarning,
    currentStepIndex,
    begin,
    requiresFollowUpConfirmation,
    cancelChange,
    continueChange,
    nextUnansweredIndex,
    nextReviewIndex,
    moveToStep,
    reset,
  }
}

function visibleReviewSteps(
  steps: GuidedModeStep[],
  answers: Record<string, GuidedAnswer>,
): VisibleReviewStep[] {
  return steps.flatMap((step, topLevelIndex) => {
    const key = stepKey(step)
    return [
      { key, step, topLevelIndex },
      ...visibleNestedReviewSteps(step.steps || [], answers, key, topLevelIndex),
    ]
  })
}

function visibleNestedReviewSteps(
  steps: GuidedModeStep[],
  answers: Record<string, GuidedAnswer>,
  parentKey: string,
  topLevelIndex: number,
): VisibleReviewStep[] {
  return steps
    .filter(step => isStepVisible(step, answers))
    .flatMap(step => {
      const key = `${parentKey}>${stepKey(step)}`
      return [
        { key, step, topLevelIndex },
        ...visibleNestedReviewSteps(step.steps || [], answers, key, topLevelIndex),
      ]
    })
}

function cloneAnswers(source: Record<string, GuidedAnswer>) {
  return Object.fromEntries(Object.entries(source).map(([conceptId, answer]) => (
    [conceptId, Array.isArray(answer) ? [...answer] : answer]
  )))
}
